import { useLayoutEffect, useRef, useState } from 'react';

import { SPAN_TYPES, TAG_META } from '../../lib/spans.js';
import { Keycap } from '../common/index.jsx';

/**
 * Floating tag menu anchored to the current selection.
 *
 * When the selection collides with an existing tag we say so and offer to jump to it,
 * rather than silently discarding the selection the way the legacy tool did.
 */
export function SelectionPopover({ selection, overlap, onTag, onGoToOverlap, onDismiss }) {
  const ref = useRef(null);
  const [position, setPosition] = useState({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    const node = ref.current;
    const rect = selection?.rect;
    if (!node || !rect) return;

    const width = node.offsetWidth;
    const height = node.offsetHeight;
    const gap = 8;

    // Prefer above the selection; flip below when there is no room.
    let top = rect.top - height - gap;
    if (top < gap) top = rect.bottom + gap;

    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(gap, Math.min(left, window.innerWidth - width - gap));

    setPosition({ top, left });
  }, [selection]);

  if (!selection) return null;

  return (
    <div
      ref={ref}
      className="selection-popover"
      style={{ top: position.top, left: position.left }}
      // Never let a click here collapse the selection we are about to read.
      onMouseDown={(event) => event.preventDefault()}
      role="toolbar"
      aria-label="Tag the selected text"
    >
      {overlap ? (
        <>
          <span className="overlap-note">
            Overlaps an existing <strong>{TAG_META[overlap.label]?.label}</strong> tag.
          </span>
          <button type="button" className="btn sm" onClick={onGoToOverlap}>
            Show it
          </button>
          <button type="button" className="btn sm ghost" onClick={onDismiss}>
            Cancel
          </button>
        </>
      ) : (
        SPAN_TYPES.map((label) => {
          const meta = TAG_META[label];
          return (
            <button
              key={label}
              type="button"
              className={`tag-btn ${label}`}
              title={meta.help}
              onClick={() => onTag(label)}
            >
              {meta.label}
              <Keycap>{meta.hotkey}</Keycap>
            </button>
          );
        })
      )}
    </div>
  );
}
